import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { downloadCsv } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TRACKS, fmtDate, scoreColor } from "@/lib/constants";
import { TrackBadge } from "@/components/TrackBadge";
import { ScoreRing } from "@/components/ScoreBar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip, CartesianGrid } from "recharts";
import {
  Inbox, CheckCircle2, Send, MessageSquare, Trophy, TrendingUp, ArrowRight, Download,
  Bell, ScanSearch, Library, ShieldCheck, ChevronRight, Sparkles, AlertCircle,
} from "lucide-react";

const FUNNEL = [
  { key: "sourced", label: "Sourced", icon: Inbox, color: "#3b82f6" },
  { key: "approved", label: "Approved", icon: CheckCircle2, color: "#8b5cf6" },
  { key: "submitted", label: "Submitted", icon: Send, color: "#14b8a6" },
  { key: "replied", label: "Replied", icon: MessageSquare, color: "#f59e0b" },
  { key: "booked", label: "Booked", icon: Trophy, color: "#10b981" },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: funnel } = useQuery({ queryKey: ["funnel"], queryFn: async () => (await api.get("/reports/funnel")).data });
  const { data: tracks = [] } = useQuery({ queryKey: ["track-report"], queryFn: async () => (await api.get("/reports/tracks")).data });
  const { data: opps = [] } = useQuery({ queryKey: ["opps-dash"], queryFn: async () => (await api.get("/opportunities")).data });
  const { data: outreach = [] } = useQuery({ queryKey: ["q-outreach-nav"], queryFn: async () => (await api.get("/approvals/queue?kind=outreach")).data });
  const { data: submission = [] } = useQuery({ queryKey: ["q-submission-nav"], queryFn: async () => (await api.get("/approvals/queue?kind=submission")).data });
  const { data: reminders = [] } = useQuery({ queryKey: ["reminders"], queryFn: async () => (await api.get("/reminders")).data });

  const firstName = (user?.name || user?.email || "there").split(/[ @]/)[0];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  const top = [...opps].sort((a, b) => (b.scores?.total || 0) - (a.scores?.total || 0)).slice(0, 5);

  const newCount = opps.filter((o) => o.status === "new").length;
  const approvalsCount = outreach.length + submission.length;
  const soon = new Date(); soon.setDate(soon.getDate() + 7);
  const dueReminders = reminders.filter((r) => !r.done && r.due_date && new Date(r.due_date) <= soon);

  const attention = [
    { count: newCount, label: "New to triage", hint: "review & promote", icon: Inbox, color: "#3b82f6", to: "/triage", testid: "attn-triage" },
    { count: approvalsCount, label: "Awaiting approval", hint: "outreach & submissions", icon: CheckCircle2, color: "#8b5cf6", to: "/approvals", testid: "attn-approvals" },
    { count: dueReminders.length, label: "Follow-ups due", hint: "next 7 days", icon: Bell, color: "#f59e0b", to: "/crm", testid: "attn-followups" },
  ];
  const totalAttention = attention.reduce((s, a) => s + a.count, 0);

  const quickActions = [
    { label: "Search gigs", icon: ScanSearch, to: "/triage", testid: "qa-search" },
    { label: "Asset bank", icon: Library, to: "/assets", testid: "qa-assets" },
    { label: "Compliance", icon: ShieldCheck, to: "/compliance", testid: "qa-compliance" },
  ];

  const f = funnel?.funnel || {};
  const convRate = f.sourced ? Math.round(((f.booked || 0) / f.sourced) * 100) : 0;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-7" data-testid="dashboard-view">
      {/* Greeting */}
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{greeting}, {firstName} 👋</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {totalAttention > 0
              ? `You have ${totalAttention} item${totalAttention === 1 ? "" : "s"} waiting on you today.`
              : "You're all caught up — nothing needs your attention right now."}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="gap-2" data-testid="export-funnel-btn"
            onClick={async () => { try { await downloadCsv("/export/funnel.csv", "cadence_funnel.csv"); toast.success("Funnel CSV downloaded"); } catch { toast.error("Export failed"); } }}>
            <Download className="h-4 w-4" /> Export
          </Button>
          <Button className="gap-2" data-testid="dash-open-triage" onClick={() => navigate("/triage")}>
            <Inbox className="h-4 w-4" /> Open triage
          </Button>
        </div>
      </header>

      {/* Needs your attention */}
      <section className="grid sm:grid-cols-3 gap-4">
        {attention.map(({ count, label, hint, icon: Icon, color, to, testid }, i) => (
          <button key={label} onClick={() => navigate(to)} data-testid={testid}
            className={`group text-left rounded-xl border p-5 transition-all animate-in-up hover:-translate-y-0.5 ${
              count > 0 ? "border-border bg-card hover:border-primary/40" : "border-border/60 bg-card/40"
            }`} style={{ animationDelay: `${i * 60}ms` }}>
            <div className="flex items-start justify-between">
              <div className="grid h-10 w-10 place-items-center rounded-lg" style={{ background: `${color}22`, border: `1px solid ${color}44` }}>
                <Icon className="h-5 w-5" style={{ color }} />
              </div>
              {count > 0 && <span className="relative flex h-2.5 w-2.5 mt-1"><span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" style={{ background: color }} /><span className="relative inline-flex h-2.5 w-2.5 rounded-full" style={{ background: color }} /></span>}
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-mono font-bold text-3xl">{count}</span>
              <span className="text-sm font-medium">{label}</span>
            </div>
            <div className="mt-1 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{hint}</span>
              <span className="text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">Open <ChevronRight className="h-3 w-3" /></span>
            </div>
          </button>
        ))}
      </section>

      {/* Quick actions */}
      <section className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground mr-1">Quick actions:</span>
        {quickActions.map(({ label, icon: Icon, to, testid }) => (
          <button key={label} onClick={() => navigate(to)} data-testid={testid}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium hover:border-primary/40 hover:text-primary transition-colors">
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </section>

      {/* Pipeline funnel */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-semibold">Booking pipeline</h2>
          <div className="flex items-center gap-4">
            <div className="text-xs text-muted-foreground">Pipeline value <span className="font-mono font-semibold text-foreground">${(funnel?.pipeline_value || 0).toLocaleString()}</span></div>
            <div className="text-xs text-muted-foreground">Sourced → booked <span className={`font-mono font-semibold ${scoreColor(convRate)}`}>{convRate}%</span></div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {FUNNEL.map(({ key, label, icon: Icon, color }, i) => (
            <button key={key} data-testid={`kpi-${key}`} onClick={() => navigate("/crm")}
              className="text-left rounded-xl border border-border bg-card p-4 hover:border-primary/40 hover:-translate-y-0.5 transition-all animate-in-up" style={{ animationDelay: `${i * 50}ms` }}>
              <div className="flex items-center justify-between">
                <Icon className="h-4 w-4" style={{ color }} />
                <span className="text-[11px] text-muted-foreground">{label}</span>
              </div>
              <div className="font-mono font-bold text-3xl mt-2">{f[key] ?? 0}</div>
              <div className="h-1 rounded-full mt-2" style={{ background: `${color}33` }}>
                <div className="h-full rounded-full" style={{ background: color, width: `${f.sourced ? Math.round(((f[key] || 0) / f.sourced) * 100) : 0}%` }} />
              </div>
            </button>
          ))}
        </div>
      </section>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Track distribution */}
        <Card className="lg:col-span-3 p-5 border-border">
          <h2 className="font-heading text-lg font-semibold mb-1">Where your opportunities come from</h2>
          <p className="text-xs text-muted-foreground mb-4">Count by track, with average fit score.</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={tracks} margin={{ left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => v.split(" ")[0]} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <Tooltip cursor={{ fill: "hsl(var(--accent))" }} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="count" name="Opportunities" radius={[6, 6, 0, 0]}>
                {tracks.map((t) => <Cell key={t.track} fill={TRACKS[t.track]?.dot || "#3b82f6"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
            {tracks.map((t) => (
              <div key={t.track} className="rounded-lg border border-border p-3">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                  <span className="h-2 w-2 rounded-full" style={{ background: TRACKS[t.track]?.dot }} /> {TRACKS[t.track]?.short}
                </div>
                <div className="font-mono font-bold text-lg">{t.count} <span className={`text-xs ${scoreColor(t.avg_score)}`}>· {t.avg_score} fit</span></div>
              </div>
            ))}
          </div>
        </Card>

        {/* Top fit queue */}
        <Card className="lg:col-span-2 p-5 border-border flex flex-col">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-heading text-lg font-semibold">Best fits right now</h2>
            <button onClick={() => navigate("/triage")} data-testid="dash-view-triage" className="text-xs text-primary hover:underline flex items-center gap-1">
              All <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Your highest-scoring opportunities.</p>
          <div className="space-y-2 flex-1">
            {top.map((o) => (
              <button key={o.id} onClick={() => navigate(`/opportunity/${o.id}`)} data-testid={`dash-opp-${o.id}`}
                className="w-full flex items-center gap-3 rounded-lg border border-border p-3 text-left hover:bg-accent hover:border-primary/30 transition-colors">
                <ScoreRing score={o.scores?.total || 0} size={44} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{o.title}</div>
                  <div className="text-xs text-muted-foreground truncate">{o.org_name} · {fmtDate(o.deadline)}</div>
                </div>
                <TrackBadge track={o.track} short />
              </button>
            ))}
            {top.length === 0 && (
              <div className="rounded-lg border border-dashed border-border py-10 text-center">
                <Sparkles className="h-6 w-6 mx-auto text-primary mb-2" />
                <p className="text-sm text-muted-foreground">No opportunities yet.</p>
                <Button size="sm" variant="outline" className="mt-3 gap-1.5" onClick={() => navigate("/triage")}><ScanSearch className="h-4 w-4" /> Search for gigs</Button>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
